import { useEffect, useRef, useState } from "react";
import { X } from "lucide-react";
import { useFocusTrap } from "../lib/shortcuts";
import { ErrorState, Loading } from "./States";

/* Panneau de détail. Deux variantes :
   - "overlay" : tiroir modal par-dessus la page (mobile, ou ouverture depuis une table) ;
   - "docked"  : colonne ancrée dans AdminShell, sans piège de focus ni fond. */
export default function DetailDrawer({
  open,
  onClose,
  title,
  subtitle,
  badge,
  tabs = [],
  activeTab,
  onTabChange,
  actions,
  footer,
  children,
  loading = false,
  error = null,
  onRetry,
  variant = "overlay",
  testid = "detail-drawer",
}) {
  const [internalTab, setInternalTab] = useState(tabs[0]?.id ?? null);
  const panel = useRef(null);
  const modal = variant === "overlay";
  useFocusTrap(panel, open && modal);

  useEffect(() => {
    if (open && tabs.length && !tabs.some(t => t.id === internalTab)) setInternalTab(tabs[0].id);
  }, [open, tabs, internalTab]);

  if (!open) return null;

  const current = activeTab ?? internalTab;
  const select = id => { setInternalTab(id); onTabChange?.(id); };
  const active = tabs.find(t => t.id === current);

  const body = (
    <section
      ref={panel}
      className={`adm-drawer${modal ? "" : " is-docked"}`}
      role={modal ? "dialog" : "complementary"}
      aria-modal={modal ? "true" : undefined}
      aria-labelledby={`${testid}-title`}
      tabIndex={-1}
      data-testid={testid}
    >
      <header className="adm-drawer-head">
        <div>
          <h2 id={`${testid}-title`}>{title}{badge}</h2>
          {subtitle && <p>{subtitle}</p>}
        </div>
        {actions}
        <button type="button" className="adm-btn is-ghost is-icon" onClick={onClose} aria-label="Fermer le panneau" data-testid={`${testid}-close`}>
          <X size={15} />
        </button>
      </header>

      {tabs.length > 0 && (
        <div className="adm-drawer-tabs" role="tablist" aria-label="Sections de la fiche">
          {tabs.map(tab => (
            <button
              key={tab.id}
              type="button"
              role="tab"
              id={`${testid}-tab-${tab.id}`}
              aria-selected={tab.id === current}
              aria-controls={`${testid}-panel`}
              className={tab.id === current ? "is-active" : undefined}
              onClick={() => select(tab.id)}
              data-testid={`${testid}-tab-${tab.id}`}
            >
              {tab.label}
            </button>
          ))}
        </div>
      )}

      <div
        className="adm-drawer-body"
        id={`${testid}-panel`}
        role={tabs.length ? "tabpanel" : undefined}
        aria-labelledby={tabs.length ? `${testid}-tab-${current}` : undefined}
      >
        {loading && <Loading label="Chargement de la fiche…" />}
        {!loading && error && <ErrorState error={error} onRetry={onRetry} />}
        {!loading && !error && (active ? active.content : children)}
      </div>

      {footer && <footer className="adm-drawer-foot">{footer}</footer>}
    </section>
  );

  if (!modal) return body;

  return (
    <div
      className="adm-drawer-backdrop"
      onMouseDown={e => { if (e.target === e.currentTarget) onClose?.(); }}
      onKeyDown={e => { if (e.key === "Escape") { e.stopPropagation(); e.preventDefault(); onClose?.(); } }}
    >
      {body}
    </div>
  );
}

// Liste clé / valeur, la présentation par défaut d'une fiche.
export function KeyValues({ items = [] }) {
  return (
    <dl className="adm-kv">
      {items.map(item => (
        <div key={item.label} style={{ display: "contents" }}>
          <dt>{item.label}</dt>
          <dd>{item.value ?? <span style={{ color: "var(--adm-ink-4)" }}>—</span>}</dd>
        </div>
      ))}
    </dl>
  );
}

export function DrawerSection({ title, children, action }) {
  return (
    <div className="adm-section">
      <h3 style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 10 }}>
        <span>{title}</span>{action}
      </h3>
      {children}
    </div>
  );
}
